import type { RouteLocationNormalizedLoaded } from 'vue-router'

export type FeedbackContextType =
  | 'NONE'
  | 'PRODUCT'
  | 'WF_TASK'
  | 'IP_MATERIAL'
  | 'AUDIT_PRODUCT'
  | 'AUDIT_MATERIAL'
  | 'AUDIT_IP_MATERIAL'
  | 'TIKTOK_PUBLISH'

export interface FeedbackPageContext {
  type: FeedbackContextType
  id: string
}

let registered: FeedbackPageContext | null = null

export function setFeedbackContext(ctx: FeedbackPageContext | null) {
  if (!ctx?.type || !ctx.id) {
    registered = null
    return
  }
  registered = { type: ctx.type, id: String(ctx.id) }
}

export function clearFeedbackContext() {
  registered = null
}

export function getRegisteredFeedbackContext() {
  return registered
}

export function inferFeedbackContextFromRoute(route: RouteLocationNormalizedLoaded): FeedbackPageContext | null {
  const path = route.path || ''
  const queryId = firstQuery(route.query.productId)
  const taskQueryId = firstQuery(route.query.taskId)
  const paramId = route.params.id != null ? String(route.params.id) : ''

  // 先匹配更具体的路径，避免 /product、/generation 误伤 audit / video-generation / batch-generation
  if (path.includes('/video-generation') && queryId) {
    return { type: 'PRODUCT', id: queryId }
  }
  if (path.includes('/audit/product/') && paramId) {
    return { type: 'AUDIT_PRODUCT', id: paramId }
  }
  if (path.includes('/audit/ip-material/') && paramId) {
    return { type: 'AUDIT_IP_MATERIAL', id: paramId }
  }
  if (path.includes('/audit/material/') && paramId) {
    return { type: 'AUDIT_MATERIAL', id: paramId }
  }
  if (/\/ip-material(\/edit)?\//.test(path) && paramId) {
    return { type: 'IP_MATERIAL', id: paramId }
  }
  if ((path === '/product' || path.startsWith('/product/')) && queryId) {
    return { type: 'PRODUCT', id: queryId }
  }
  if (path.includes('/publish-task') && taskQueryId) {
    return { type: 'TIKTOK_PUBLISH', id: taskQueryId }
  }
  // 仅生成记录页，勿匹配 video-generation / batch-generation
  if ((path === '/generation' || path.startsWith('/generation/')) && taskQueryId) {
    return { type: 'WF_TASK', id: taskQueryId }
  }
  return null
}

export function collectFeedbackClientContext(route: RouteLocationNormalizedLoaded) {
  const pageCtx = registered || inferFeedbackContextFromRoute(route)
  const ua = navigator.userAgent || ''
  return {
    pageUrl: String(window.location.href || '').slice(0, 1000),
    route: route.fullPath || route.path,
    browser: parseBrowser(ua),
    os: parseOs(ua),
    userAgent: ua.slice(0, 500),
    frontendVersion: String(import.meta.env.VITE_APP_VERSION || ''),
    screenWidth: window.screen?.width || undefined,
    screenHeight: window.screen?.height || undefined,
    language: navigator.language || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    contextType: pageCtx?.type || 'NONE',
    contextId: pageCtx?.id || undefined
  }
}

function firstQuery(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] || '')
  if (value == null) return ''
  return String(value)
}

function parseBrowser(ua: string) {
  if (/Edg\//i.test(ua)) {
    const m = ua.match(/Edg\/([\d.]+)/)
    return `Edge ${m?.[1] || ''}`.trim()
  }
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) {
    const m = ua.match(/Chrome\/([\d.]+)/)
    return `Chrome ${m?.[1] || ''}`.trim()
  }
  if (/Firefox\//i.test(ua)) {
    const m = ua.match(/Firefox\/([\d.]+)/)
    return `Firefox ${m?.[1] || ''}`.trim()
  }
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) {
    const m = ua.match(/Version\/([\d.]+)/)
    return `Safari ${m?.[1] || ''}`.trim()
  }
  return 'Unknown'
}

function parseOs(ua: string) {
  if (/Windows NT 10/i.test(ua)) return 'Windows 10/11'
  if (/Windows NT 6.3/i.test(ua)) return 'Windows 8.1'
  if (/Mac OS X/i.test(ua)) return 'macOS'
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad/i.test(ua)) return 'iOS'
  if (/Linux/i.test(ua)) return 'Linux'
  return 'Unknown'
}
